/**
 * AP-37 Detailblatt O 6.1, 9.2 — **Dialog „Preisanfrage erzeugen"**: links die
 * Einstellungen, rechts die Vorschau je Beleg (jede Änderung ruft `dryRun`),
 * unten die zurückgehaltenen Zeilen. „Anlegen" legt je Beleg einen Entwurf im
 * Belegwesen an; „anlegen und senden" reicht die neuen Belege an den Versand
 * weiter (O3).
 *
 * Nachtrag 2 (O-74 … O-76): **Eine Empfängerliste** statt „je Lieferant der Zeilen" /
 * „gewählte Lieferanten". Vorgeschlagen sind die Lieferanten der Materialzeilen (angehakt)
 * und des Artikelstamms; weitere kommen über die Suche dazu. Jeder Empfänger bekommt alle
 * gewählten Zeilen — einzelne nimmt das ✕ in der Vorschau heraus.
 *
 * Aufbau (29.9.2026): gemeinsames `Modal` mit festem Kopf und Fuß; die Einstellungen
 * stehen in drei Abschnitten (Empfänger · Aufbau der Belege · Angaben im Beleg), beide
 * Spalten scrollen für sich.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, FileQuestion, Loader2, Paperclip, Search, Send, Settings2, Star, X } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { apiErrorText } from '@/lib/api-error'
import { fieldLabel, groupLabel, inputBase, selectBase } from '@/lib/form-styles'
import { lieferantenSuchen, preisanfragenErzeugen } from '../../api/calculation-requests.api'
import type { CreateRequestsParams, CreateRequestsResult, HeldReason, MaterialRow, RequestMerge, RequestSuggestion } from '../../types/calculation-requests.types'

const GRUND: Record<HeldReason, string> = {
  NO_SUPPLIER: 'kein Lieferant',
  NOT_SUPPLIER: 'Partner ohne Lieferantenrolle',
  ALREADY: 'schon angefragt',
  NO_UNIT: 'Einheit fehlt',
  SKIPPED: '„nicht anfragen" gesetzt',
}

const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })
const groesse = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(b / 1024))} kB`)
const tagDe = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
const mehrzahl = (anzahl: number, eins: string, viele: string) => `${anzahl} ${anzahl === 1 ? eins : viele}`

const kaestchen = 'mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]'
const option = 'flex cursor-pointer items-start gap-2'
const feld = 'py-1.5 text-sm'

export function PreisanfrageDialog({
  zeilen,
  onClose,
  onAngelegt,
  sendenMoeglich = false,
}: {
  zeilen: MaterialRow[]
  /** O3: „anlegen und senden" anbieten */
  sendenMoeglich?: boolean
  onClose: () => void
  /** nach dem Anlegen; `senden` = „anlegen und senden" gewählt */
  onAngelegt: (ergebnis: CreateRequestsResult, senden: boolean) => void
}) {
  // Nachtrag 2: Bis die Vorschläge da sind, rechnet die Vorschau wie bisher je Lieferant der Zeilen.
  const [vorschlaege, setVorschlaege] = useState<RequestSuggestion[] | null>(null)
  const [gewaehlt, setGewaehlt] = useState<{ id: string; name: string }[]>([])
  const [entfernt, setEntfernt] = useState<{ supplierId: string; costLineId: string }[]>([])
  const [merge, setMerge] = useState<RequestMerge | undefined>(undefined)
  const [split, setSplit] = useState<'PER_SUPPLIER' | 'PER_SUPPLIER_AND_CALCULATION'>('PER_SUPPLIER')
  const [titles, setTitles] = useState(true)
  const [descriptions, setDescriptions] = useState(true)
  const [dueAt, setDueAt] = useState<string>('')
  const [commission, setCommission] = useState<string | undefined>(undefined)
  const [withFiles, setWithFiles] = useState(true)
  const [abgehakt, setAbgehakt] = useState<Set<string>>(new Set())
  const [vorschau, setVorschau] = useState<CreateRequestsResult | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  // Nachtrag 7 (N7-8): Die Liste der Lieferanten klappt schon beim Klick ins Feld auf, nicht erst beim Tippen.
  const [sucheOffen, setSucheOffen] = useState(false)
  const [treffer, setTreffer] = useState<{ id: string; name: string; clientNumber: string }[]>([])
  const zaehler = useRef(0)

  const bereit = vorschlaege !== null
  const params = useMemo<CreateRequestsParams>(
    () => ({
      costLineIds: zeilen.map((z) => z.costLineId),
      recipients: bereit ? 'CHOSEN' : 'BY_LINE_SUPPLIER',
      supplierIds: bereit ? gewaehlt.map((g) => g.id) : undefined,
      excludedLines: entfernt,
      merge,
      split,
      titles,
      descriptions,
      dueAt: dueAt || undefined,
      commission,
      withFiles,
      excludedFileIds: [...abgehakt],
    }),
    [zeilen, bereit, gewaehlt, entfernt, merge, split, titles, descriptions, dueAt, commission, withFiles, abgehakt],
  )
  const wahlFehlt = bereit && gewaehlt.length === 0

  // Vorschau bei jeder Änderung (Muster Einkauf), die letzte Antwort gewinnt.
  useEffect(() => {
    if (wahlFehlt) {
      setVorschau(null)
      return
    }
    const nr = ++zaehler.current
    const t = setTimeout(() => {
      setLaedt(true)
      preisanfragenErzeugen({ ...params, dryRun: true })
        .then((r) => {
          if (nr !== zaehler.current) return
          setVorschau(r)
          setFehler(null)
          if (merge === undefined) setMerge(r.merge)
          // Erste Antwort: Vorschläge merken, die Lieferanten der Zeilen sind angehakt (O-75).
          if (vorschlaege === null) {
            setVorschlaege(r.suggestions ?? [])
            setGewaehlt((r.suggestions ?? []).filter((s) => s.lineCount > 0).map((s) => ({ id: s.id, name: s.name })))
          }
        })
        .catch((e) => nr === zaehler.current && setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht rechnen.')))
        .finally(() => nr === zaehler.current && setLaedt(false))
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, wahlFehlt])

  useEffect(() => {
    if (!bereit) return
    const t = setTimeout(() => {
      lieferantenSuchen(suche || undefined)
        .then(setTreffer)
        .catch(() => setTreffer([]))
    }, 250)
    return () => clearTimeout(t)
  }, [suche, bereit])

  const listeOffen = sucheOffen || Boolean(suche.trim())
  // Esc schließt zuerst die Liste, erst dann den Dialog. Der Dialog fängt Esc in der Capture-Phase am
  // Dokument — wer davor dran sein will, muss am Fenster hören.
  useEffect(() => {
    if (!listeOffen) return
    const aufEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setSucheOffen(false)
      setSuche('')
    }
    window.addEventListener('keydown', aufEsc, true)
    return () => window.removeEventListener('keydown', aufEsc, true)
  }, [listeOffen])

  const istGewaehlt = (id: string) => gewaehlt.some((g) => g.id === id)
  const umschalten = (e: { id: string; name: string }) =>
    setGewaehlt((alt) => (alt.some((g) => g.id === e.id) ? alt.filter((g) => g.id !== e.id) : [...alt, { id: e.id, name: e.name }]))
  const entferntBei = (supplierId: string) => entfernt.filter((x) => x.supplierId === supplierId).length
  const zurueckholen = (supplierId: string) => setEntfernt((alt) => alt.filter((x) => x.supplierId !== supplierId))
  const n = zeilen.length
  // Der Vorzugslieferant steht als Abzeichen am Namen, nicht im Hinweis.
  const herkunft = (s: RequestSuggestion) =>
    s.lineCount > 0 ? `an der Materialzeile hinterlegt · ${s.lineCount} von ${n} Zeilen` : `im Artikelstamm als Lieferant · ${s.articleCount} von ${n} Zeilen`
  const vonHand = gewaehlt.filter((g) => !vorschlaege?.some((s) => s.id === g.id))
  const empfaenger = [
    ...(vorschlaege ?? []).map((s) => ({ id: s.id, name: s.name, hinweis: herkunft(s), vorzug: s.preferred })),
    ...vonHand.map((g) => ({ id: g.id, name: g.name, hinweis: 'selbst hinzugefügt', vorzug: false })),
  ]
  const weitereTreffer = treffer.filter((t) => !istGewaehlt(t.id))

  const anlegen = async (senden: boolean) => {
    setLaedt(true)
    setFehler(null)
    try {
      const r = await preisanfragenErzeugen({ ...params, dryRun: false })
      onAngelegt(r, senden)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Preisanfragen ließen sich nicht anlegen.'))
    } finally {
      setLaedt(false)
    }
  }

  const belege = vorschau?.documents ?? []
  const fristVorgabe = belege[0]?.dueAt ?? ''
  const zeilenGesamt = belege.reduce((summe, b) => summe + b.lines.length, 0)
  const zurueckgehalten = vorschau?.held ?? []
  const nichtsAnzufragen = wahlFehlt || (vorschau !== null && belege.length === 0)

  return (
    <Modal
      isOpen
      onClose={onClose}
      scrollBody={false}
      className="h-[85vh] max-w-6xl"
      title={
        <span className="text-primary">
          Preisanfrage erzeugen
          <span className="block text-xs font-normal text-muted-foreground">
            {mehrzahl(n, 'Materialzeile', 'Materialzeilen')} gewählt — je Beleg ein Entwurf „Preisanfrage" im Belegwesen, ohne Preise.
          </span>
        </span>
      }
      footer={
        <>
          <Link to="/calculations/settings" className="mr-auto inline-flex items-center gap-1 self-center text-xs text-muted-foreground hover:text-foreground hover:underline">
            <Settings2 className="h-3.5 w-3.5" />
            Vorgaben in den Einstellungen
          </Link>
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className={
              sendenMoeglich
                ? 'rounded-md border border-primary px-4 py-2 text-sm text-primary hover:bg-primary/10 disabled:opacity-50'
                : 'rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
            }
            disabled={laedt || !belege.length}
            onClick={() => void anlegen(false)}
          >
            {belege.length ? `${mehrzahl(belege.length, 'Preisanfrage', 'Preisanfragen')} anlegen` : 'Preisanfragen anlegen'}
          </button>
          {sendenMoeglich && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              disabled={laedt || !belege.length}
              onClick={() => void anlegen(true)}
            >
              <Send className="h-4 w-4" />
              Anlegen und senden
            </button>
          )}
        </>
      }
    >
      <div className="grid min-h-0 flex-1 grid-rows-[auto_1fr] overflow-y-auto lg:grid-cols-[22rem_1fr] lg:grid-rows-1 lg:overflow-hidden">
        {/* Einstellungen — wer bekommt die Anfrage, wie sind die Belege aufgebaut, was steht darin */}
        <aside className="space-y-6 border-b border-border bg-muted/20 p-5 text-sm lg:overflow-y-auto lg:border-b-0 lg:border-r">
          <section className="space-y-2">
            <h3 className={groupLabel}>Empfänger</h3>
            <p className="text-xs text-muted-foreground">
              Jeder angehakte Lieferant bekommt alle gewählten Materialzeilen. Einzelne Zeilen nimmst du rechts in der Vorschau mit ✕ heraus.
            </p>
            {!bereit && <p className="text-xs text-muted-foreground">Vorschläge werden geladen …</p>}
            {bereit && empfaenger.length === 0 && (
              <p className="text-xs text-muted-foreground">Keine Vorschläge — weder an den Materialzeilen noch im Artikelstamm ist ein Lieferant hinterlegt.</p>
            )}
            {empfaenger.length > 0 && (
              <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
                {empfaenger.map((e) => (
                  <label key={e.id} className={`${option} px-3 py-2 hover:bg-muted/40`}>
                    <input type="checkbox" className={kaestchen} checked={istGewaehlt(e.id)} onChange={() => umschalten(e)} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{e.name}</span>
                        {e.vorzug && (
                          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary" title="Vorzugslieferant">
                            <Star className="h-2.5 w-2.5" />
                            Vorzugslieferant
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {e.hinweis}
                        {istGewaehlt(e.id) && entferntBei(e.id) > 0 && (
                          <>
                            {' · '}
                            {mehrzahl(entferntBei(e.id), 'Zeile', 'Zeilen')} nicht angefragt{' '}
                            <button type="button" className="text-primary hover:underline" onClick={() => zurueckholen(e.id)}>
                              wieder anfragen
                            </button>
                          </>
                        )}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            {bereit && (
              <div
                // Zu geht die Liste, wenn der Fokus Feld und Liste verlässt — nicht beim Klick auf einen Eintrag.
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSucheOffen(false)
                }}
              >
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className={`${inputBase} ${feld} pl-8`}
                    placeholder="Weiteren Lieferanten hinzufügen …"
                    role="combobox"
                    aria-expanded={listeOffen}
                    aria-controls="preisanfrage-lieferanten"
                    aria-autocomplete="list"
                    value={suche}
                    onChange={(e) => {
                      setSuche(e.target.value)
                      setSucheOffen(true)
                    }}
                    onFocus={() => setSucheOffen(true)}
                    onClick={() => setSucheOffen(true)}
                  />
                </div>
                {listeOffen && (
                  <div
                    id="preisanfrage-lieferanten"
                    className="mt-1 max-h-40 overflow-auto rounded-md border border-border bg-background shadow-sm"
                    // Ein Druck auf den Rollbalken der Liste nimmt dem Suchfeld den Fokus nicht: Die Liste ging sonst
                    // zu, bevor sie sich rollen ließ (Auftraggeber 13.10.2026). Der Klick auf einen Eintrag kommt trotzdem an.
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {weitereTreffer.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted"
                        onClick={() => {
                          setGewaehlt((alt) => [...alt, { id: t.id, name: t.name }])
                          setSuche('')
                          setSucheOffen(false)
                        }}
                      >
                        <span className="truncate">{t.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{t.clientNumber}</span>
                      </button>
                    ))}
                    {weitereTreffer.length === 0 && <p className="px-3 py-1.5 text-xs text-muted-foreground">Kein weiterer Lieferant gefunden.</p>}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h3 className={groupLabel}>Aufbau der Belege</h3>
            <div className="space-y-1.5">
              <label htmlFor="pa-zusammenfassen" className={fieldLabel}>
                Zusammenfassen
              </label>
              <select id="pa-zusammenfassen" className={`${selectBase} ${feld}`} value={merge ?? ''} onChange={(e) => setMerge(e.target.value as RequestMerge)}>
                <option value="PER_CALCULATION">je Kalkulation</option>
                <option value="ACROSS">über alle Kalkulationen</option>
                <option value="NONE">nicht zusammenfassen</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="pa-aufteilen" className={fieldLabel}>
                Aufteilen
              </label>
              <select id="pa-aufteilen" className={`${selectBase} ${feld}`} value={split} onChange={(e) => setSplit(e.target.value as typeof split)}>
                <option value="PER_SUPPLIER">ein Beleg je Lieferant</option>
                <option value="PER_SUPPLIER_AND_CALCULATION">ein Beleg je Lieferant und Kalkulation</option>
              </select>
            </div>
            <div className="space-y-2 pt-1">
              <label className={`${option} ${merge === 'ACROSS' ? 'cursor-not-allowed opacity-50' : ''}`}>
                <input type="checkbox" className={kaestchen} checked={titles && merge !== 'ACROSS'} disabled={merge === 'ACROSS'} onChange={(e) => setTitles(e.target.checked)} />
                <span>Titelzeile je Kalkulation (Bauvorhaben, Ort)</span>
              </label>
              <label className={option}>
                <input type="checkbox" className={kaestchen} checked={descriptions} onChange={(e) => setDescriptions(e.target.checked)} />
                <span>Beschreibungen übernehmen</span>
              </label>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className={groupLabel}>Angaben im Beleg</h3>
            <div className="space-y-1.5">
              <label htmlFor="pa-frist" className={fieldLabel}>
                Antwort bis
              </label>
              <input id="pa-frist" type="date" className={`${inputBase} ${feld}`} value={dueAt || fristVorgabe} onChange={(e) => setDueAt(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="pa-kommission" className={fieldLabel}>
                Kommission
              </label>
              <input
                id="pa-kommission"
                className={`${inputBase} ${feld}`}
                value={commission ?? belege[0]?.commission ?? ''}
                onChange={(e) => setCommission(e.target.value)}
                placeholder="leer bei mehreren Kalkulationen"
              />
            </div>
            <label className={`${option} pt-1`}>
              <input type="checkbox" className={kaestchen} checked={withFiles} onChange={(e) => setWithFiles(e.target.checked)} />
              <span>Dateien der Materialzeilen mitsenden</span>
            </label>
          </section>
        </aside>

        {/* Vorschau */}
        <section className="space-y-3 p-5 lg:min-h-0 lg:overflow-y-auto">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={groupLabel}>Vorschau</h3>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {laedt && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {vorschau &&
                [
                  mehrzahl(belege.length, 'Preisanfrage', 'Preisanfragen'),
                  mehrzahl(zeilenGesamt, 'Zeile', 'Zeilen'),
                  zurueckgehalten.length ? `${zurueckgehalten.length} zurückgehalten` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
            </span>
          </div>
          {fehler && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
          {laedt && !vorschau && !wahlFehlt && <p className="text-sm text-muted-foreground">Vorschau wird gerechnet …</p>}

          {nichtsAnzufragen && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-8 text-center text-sm text-muted-foreground">
              <FileQuestion className="h-8 w-8 opacity-60" />
              {wahlFehlt ? (
                <p>Bitte links mindestens einen Lieferanten anhaken.</p>
              ) : (
                <>
                  <p className="font-medium text-foreground">Keine Zeile kann angefragt werden.</p>
                  {zurueckgehalten.length > 0 && <p>Warum, steht darunter bei den zurückgehaltenen Zeilen.</p>}
                </>
              )}
            </div>
          )}

          {belege.map((b) => (
            <article key={b.supplierId + b.calculations.join()} className="overflow-hidden rounded-lg border border-border bg-background">
              <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-b border-border bg-muted/30 px-3 py-2">
                <div className="font-medium">{b.supplierName}</div>
                <div className="text-xs text-muted-foreground">
                  {b.calculations.join(' · ')} · {mehrzahl(b.lines.length, 'Zeile', 'Zeilen')} · Antwort bis {tagDe(b.dueAt)}
                </div>
              </header>
              <table className="w-full text-sm">
                <tbody>
                  {b.lines.map((l, i) => [
                    l.title && l.title !== b.lines[i - 1]?.title ? (
                      <tr key={`t${i}`}>
                        <td colSpan={4} className="px-3 pb-1 pt-2 text-xs font-semibold text-muted-foreground">
                          {l.title}
                        </td>
                      </tr>
                    ) : null,
                    <tr key={i} className="group border-t border-border/60 align-top first:border-t-0">
                      <td className="w-24 py-1.5 pl-3 pr-2 text-right tabular-nums">{mengeText(l.quantity)}</td>
                      <td className="w-12 py-1.5 pr-2 text-muted-foreground">{l.unitCode}</td>
                      <td className="py-1.5">
                        {l.name}
                        {l.rows > 1 && <span className="ml-1 text-xs text-muted-foreground">({l.rows} Zeilen zusammengefasst)</span>}
                        {l.files > 0 && (
                          <span className="ml-1 inline-flex items-center text-xs text-muted-foreground">
                            <Paperclip className="h-3 w-3" />
                            {l.files}
                          </span>
                        )}
                        {l.alsoAt.length > 0 && <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">auch bei {l.alsoAt.join(', ')} angefragt</span>}
                        {l.description && <div className="whitespace-pre-line text-xs text-muted-foreground">{l.description}</div>}
                      </td>
                      <td className="w-8 py-1.5 pr-2 text-right">
                        <button
                          type="button"
                          className="rounded p-0.5 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive group-hover:text-muted-foreground"
                          title={`Diese Zeile nicht bei ${b.supplierName} anfragen`}
                          aria-label={`Diese Zeile nicht bei ${b.supplierName} anfragen`}
                          onClick={() => setEntfernt((alt) => [...alt, ...l.costLineIds.map((costLineId) => ({ supplierId: b.supplierId, costLineId }))])}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>,
                  ])}
                </tbody>
              </table>
              {withFiles && b.files.length > 0 && (
                <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs">
                  <div className="mb-1 font-medium">
                    Dateien ({groesse(b.filesBytes)}){b.filesAsLinkHint && ' — gehen je nach Versandweg als Link'}
                  </div>
                  {b.files.map((f) => (
                    <label key={f.calculationFileId} className="flex cursor-pointer items-center gap-2 py-0.5">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                        checked={!abgehakt.has(f.calculationFileId)}
                        onChange={(e) =>
                          setAbgehakt((alt) => {
                            const neu = new Set(alt)
                            if (e.target.checked) neu.delete(f.calculationFileId)
                            else neu.add(f.calculationFileId)
                            return neu
                          })
                        }
                      />
                      <span>{f.fileName}</span>
                      <span className="text-muted-foreground">
                        {groesse(f.sizeBytes)} · {f.line}
                      </span>
                      {f.hint === 'TYPE' && <span className="text-destructive">Dateityp wird nicht versendet</span>}
                      {f.hint === 'TOO_MANY' && <span className="text-destructive">mehr als 10 Dateien an einer Zeile</span>}
                    </label>
                  ))}
                </div>
              )}
            </article>
          ))}

          {/* abgehakte Dateien bleiben abgehakt, auch wenn die Vorschau sie gerade nicht zeigt */}
          {zurueckgehalten.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-amber-300 bg-amber-50 text-sm dark:border-amber-700 dark:bg-amber-900/30">
              <div className="flex items-center gap-2 border-b border-amber-300/70 px-3 py-2 font-medium dark:border-amber-700/70">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                Zurückgehalten ({zurueckgehalten.length}) — diese Zeilen werden nicht angefragt
              </div>
              <ul className="divide-y divide-amber-300/50 dark:divide-amber-700/50">
                {zurueckgehalten.map((h, i) => (
                  <li key={h.costLineId + i} className="px-3 py-1.5">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium">{h.name}</span>
                      <span className="text-xs text-muted-foreground">{h.calculation}</span>
                    </div>
                    <div className="text-xs text-amber-800 dark:text-amber-200">
                      {GRUND[h.reason]}
                      {h.detail ? `: ${h.detail}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}
